import { Button, Divider } from "antd";
import React from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import infoImage from "../../../Images/info-icon.svg";
import "../../../Styles/styles-Calendar.css";
import { RPE_METRICS } from "../../../Utils/calendar_utils";
import Popout from "@components/Common/Popout"
import { DateCellContent } from "./DateCellContent";
import { DataColorUtil } from "@utils/color_utils"

interface IDateInfoPopout {
	info: {
		[key: string]: any,
		date: string,
		opponent?: string,
		drills: string[],
		injuries: any[],
		lift: string,
		is_prediction?: any,
		total_time?: number,
		wellness_score?: number
		color?: string,
		prediction?: boolean
	},
	metrics: { [key: string]: any }[],
	showInfoPopout: boolean,
	setShowInfoPopout: React.Dispatch<boolean>,
	openPredictionDialog: (date: string) => void,
	handleDeletePracticePlan: () => void,
	showHelp: () => void
}

const Metrics = ({ metrics, info }: { metrics: IDateInfoPopout['metrics'], info: IDateInfoPopout['info'] }) => {
	const { colors } = useSelector(({ calendar }) => calendar);
	const { color_strategies: colorStrategies, info: { display_name: teamName } } = useSelector(({ team }) => team);

	const dataColorUtil = new DataColorUtil({
			teamName,
			gpsBaselines: colors,
			teamGpsMetrics: metrics,
			colorStrategies
		  });

	const formatNumber = (num: number, decimalPlaces: number) => {
		try {
			return Number(num)?.toFixed(decimalPlaces);
		} catch (e) {
			return '';
		}
	};

	return (
		<>
			{metrics
				.filter(({ key }) => !RPE_METRICS.includes(key))
				.filter(e => e.visible === true || e.visible === undefined)
				.map((metric, index) => {
					if (!metric || !metric.key) return null;
					let metricValue = info?.[metric.key];
					let formattedValue = metricValue || 0;
					const decimalPlaces = metric?.decimal_places ?? 2;

					return (
						<p
							key={index}
							style={{
								fontWeight: "bold",
								color: dataColorUtil.get_gps_color(metric.key, metricValue),
								margin: "4px 0",
								textAlign: 'left',
								textIndent: '-30px',
								marginLeft: '30px'
							}}
						>{`${metric.name}: ${formatNumber(formattedValue, decimalPlaces)}`}</p>
					);
				})}
		</>
	)
}

const Drills = ({ drills, dateString, prediction }: { drills: any[], dateString: string, prediction?: boolean }) => {
	return (
		<div style={{ textAlign: "left" }}>
			{drills !== null &&
				drills !== undefined &&
				drills.length !== 0 ? (
				<div>
					<ol style={{ padding: "0 0 10px 0" }}>
						{drills.map((drill, i) => (
							<div
								key={i}
								style={{
									textAlign: "left",
								}}
							>
								<li
									style={{
										listStylePosition: "inside",
									}}
								>
									{prediction ? (
										drill.name + ` (${drill.duration} minutes)`
									) : (
										<Link
											to={`/drills?drill=${drill?.trim()}&date=${dateString}`}
										>
											{drill}
										</Link>
									)}
								</li>
							</div>
						))}
					</ol>
				</div>
			) : null}
		</div>
	)
}

const Footer = ({
	showPredictionButtons,
	dateString,
	handleDeletePracticePlan,
	openPredictionDialog
}: {
	showPredictionButtons: boolean,
	dateString: string,
	handleDeletePracticePlan: IDateInfoPopout['handleDeletePracticePlan'],
	openPredictionDialog: IDateInfoPopout['openPredictionDialog']
}) => {
	const navigate = useNavigate();

	return (
		<>
			{showPredictionButtons && (
				<Button
					danger
					type="primary"
					className="delete-button"
					onClick={handleDeletePracticePlan}
				>
					<div>
						Delete predictions
					</div>
				</Button>
			)}

			<Button
				className="calendar-nav-link"
				onClick={() =>
					showPredictionButtons
						? openPredictionDialog(dateString)
						: navigate(`/breakdown/${dateString}`)
				}
			>
				{showPredictionButtons
					? "Edit Plan"
					: `Go To Breakdown`}
			</Button>
		</>
	)
}

export const DateInfoPopout = ({
	info,
	metrics,
	openPredictionDialog,
	showInfoPopout,
	setShowInfoPopout,
	handleDeletePracticePlan,
	showHelp
}: IDateInfoPopout) => {
	const {
		date: dateString,
		opponent,
		drills,
		injuries,
		lift,
		is_prediction: isPrediction,
		total_time,
		wellness_score: wellnessScore
	} = info;

	const showPredictionButtons = isPrediction && total_time === undefined;

	// Determine wellness score presentation
	let wellnessScoreClassName = "";
	if (wellnessScore !== undefined) {
		if (wellnessScore >= 75) {
			wellnessScoreClassName = "text-success";
		} else if (wellnessScore >= 50) {
			wellnessScoreClassName = "text-warning";
		} else {
			wellnessScoreClassName = "text-danger";
		}
	}

	return (
		<Popout
			showPopout={showInfoPopout}
			setShowPopout={setShowInfoPopout}
			popOutStyles={{
				maxHeight: "900px",
				width: "100%",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
			cardStyles={{
				overflow: "scroll",
				width: "90%",
				maxHeight: "800px",
				display: "flex",
				flexDirection: "column"
			}}
		>
			<div style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				maxHeight: "inherit",
				overflow: "hidden"
			}}>
				{info.color === 'blue' && info.opponent ? (
					<div>
						<p>
							Opponent: {!info["home"] ? "@" : "vs."} {info["opponent"]}
						</p>
					</div>
				) : (
					<>
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<DateCellContent
								info={info}
								date={dateString}
								showGameAndTraining={true}
								fontSize={20}
								popOutView={true}
							/>
							<div style={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
								<p style={{ fontWeight: "bold", fontSize: 18, marginBottom: '0rem' }}>{dateString}</p>
								<img
									src={infoImage}
									alt="info-img"
									style={{ cursor: "pointer", width: "20px", height: "20px" }}
									onClick={() => showHelp()}
								/>
							</div>
						</div>

						<Divider style={{ margin: "12px 0" }} />

						<div style={{
							flex: 1,
							overflow: "auto",
						}}>
							<div style={{ display: 'flex', flexDirection: 'row', 'justifyContent': 'center', columnGap: '30px', borderBottom: '1px solid lightgrey', paddingBottom: '20px' }}>
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-start",
										overflowY: 'auto',
										maxHeight: '280px',
										width: `${(drills && drills.length !== 0) ? '50%' : '80%'}`,
										borderRight: `${(drills && drills.length !== 0) && '1px solid lightgrey'}`,
									}}
								>
									<Metrics metrics={metrics} info={info} />
								</div>

								{(drills && drills.length !== 0) &&
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "flex-start",
											overflowY: 'auto',
											maxHeight: '280px',
											width: '40%',
										}}
									>
										
										<Drills prediction={info['prediction']} dateString={dateString} drills={drills} />
									</div>
								}
							</div>

							{wellnessScore && (
								<div style={{ textAlign: "left" }}>
									<p className={wellnessScoreClassName}>
										Wellness Score: {wellnessScore}
									</p>
								</div>
							)}

							{opponent && (
								<div style={{ textAlign: "left" }}>
									<p className="html-metrics">Opponent: {opponent}</p>
								</div>
							)}

							<div style={{ textAlign: "left", marginBottom: 8 }}>
								{injuries !== undefined && injuries.length !== 0 ? (
									<>
										<p>Injuries: </p>
										<ul>
											{injuries.map((injury, i) => (
												<li key={i}>{injury}</li>
											))}
										</ul>
									</>
								) : null}
							</div>

							{lift !== undefined && <div style={{ textAlign: "left" }}><p>Lift: {lift}</p></div>}
						</div>

						<div className="footer-button-container" style={{ marginTop: "12px" }}>
							<Footer
								showPredictionButtons={showPredictionButtons}
								dateString={dateString}
								handleDeletePracticePlan={handleDeletePracticePlan}
								openPredictionDialog={openPredictionDialog} />
						</div>
					</>
				)}
			</div>
		</Popout>
	)
}

export default DateInfoPopout;